import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import BaseApi from "../../../services/base-api";
import { Role } from "../../../types/Role";

const useToast = () => ({
  success: (msg: string) => console.log("✅ Success:", msg),
  err: (msg: string) => console.error("❌ Error:", msg),
});

export const useRolesData = () => {
  const { t } = useTranslation();
  const { success, err } = useToast();

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("cards");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Use ref to prevent re-creating toast functions
  const toastRef = useRef({ success, err });
  
  useEffect(() => {
    toastRef.current = { success, err };
  }, [success, err]);

  // ✅ FIXED: Fetch roles only once on mount
  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await BaseApi.get("/roles?page=1&limit=1000");
      if (res?.success && Array.isArray(res.data)) {
        setRoles(res.data);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      toastRef.current.err(t("Failed to fetch roles"));
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // ✅ Runs only ONCE on mount
  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run on mount

  // Memoized filtered roles
  
const filtered = useMemo(() => {
  try {
    let result = [...roles];

    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter((role: Role) => {
        if (!role) return false;
        
        try {
          const nameMatch = (role.name || "").toLowerCase().includes(searchLower);
          
          const displayNameMatch = 
            (role.displayName?.en || "").toLowerCase().includes(searchLower) ||
            (role.displayName?.ar || "").toLowerCase().includes(searchLower);
          
          let descMatch = false;
          if (typeof role.description === "string") {
            descMatch = role.description.toLowerCase().includes(searchLower);
          } else if (role.description && typeof role.description === "object") {
            descMatch = 
              (role.description.en || "").toLowerCase().includes(searchLower) ||
              (role.description.ar || "").toLowerCase().includes(searchLower);
          }
          
          return nameMatch || displayNameMatch || descMatch;
        } catch (error) {
          console.error("Error filtering role:", role, error);
          return false;
        }
      });
    }

    if (statusFilter !== "all") {
      result = result.filter((role: Role) => {
        if (!role) return false;
        return statusFilter === "active" ? role.isActive : !role.isActive;
      });
    }

    return result;
  } catch (error) {
    console.error("Error filtering roles:", error);
    return [];
  }
}, [roles, search, statusFilter]);


  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  // Memoized pagination
  const paginatedData = useMemo(() => {
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedItems = filtered.slice(startIndex, endIndex);
    
    return { totalPages, startIndex, endIndex, paginatedItems };
  }, [filtered, currentPage, itemsPerPage]);

  // Optimistic toggle status
  const handleToggleStatus = useCallback(
    async (role: Role) => {
      if (!role?.id) return;

      const previousState = role.isActive;

      // Optimistic update
      setRoles((prevRoles) =>
        prevRoles.map((r) =>
          r.id === role.id ? { ...r, isActive: !r.isActive } : r
        )
      );

      try {
        const res = await BaseApi.patch(`/roles/${role.id}/toggle`, {});
        if (res?.success) {
          toastRef.current.success(
            t(`Role ${!previousState ? "activated" : "deactivated"} successfully!`)
          );
        } else {
          throw new Error("Failed to toggle");
        }
      } catch (error) {
        // Rollback on error
        setRoles((prevRoles) =>
          prevRoles.map((r) =>
            r.id === role.id ? { ...r, isActive: previousState } : r
          )
        );
        console.error("Error toggling role status:", error);
        toastRef.current.err(t("Failed to toggle role status"));
      }
    },
    [t]
  );

  // Seed default roles
  const handleSeedDefaults = useCallback(async () => {
    try {
      const res = await BaseApi.post("/roles/seed-defaults", {});
      if (res?.success) {
        toastRef.current.success(t("Default roles seeded successfully!"));
        await fetchRoles();
      }
    } catch (error) {
      console.error("Error seeding default roles:", error);
      toastRef.current.err(t("Failed to seed default roles"));
    }
  }, [fetchRoles, t]);

  return {
    roles,
    filtered,
    loading,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    viewMode,
    setViewMode,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    paginatedData,
    handleToggleStatus,
    handleSeedDefaults,
    refreshRoles: fetchRoles,
  };
};